import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Hook to dynamically position emoji picker to stay within viewport bounds
 * @param {boolean} isVisible - Whether the emoji picker is currently visible
 * @returns {Object} - { pickerRef, buttonRef, position }
 */
export const useEmojiPickerPosition = (isVisible) => {
  const pickerRef = useRef(null);
  const buttonRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, placement: 'top' });

  const calculatePosition = useCallback(() => {
    if (!pickerRef.current || !buttonRef.current || !isVisible) return;

    const button = buttonRef.current.getBoundingClientRect();
    const picker = pickerRef.current.getBoundingClientRect();
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    // Define picker dimensions (approximate if not rendered yet)
    const pickerWidth = picker.width || 350; // Default emoji picker width
    const pickerHeight = picker.height || 450; // Default emoji picker height
    const gap = 8; // Gap between button and picker

    // Calculate available space in all directions
    const spaceAbove = button.top;
    const spaceBelow = viewport.height - button.bottom;
    const spaceLeft = button.left;
    const spaceRight = viewport.width - button.right;

    let top = 0;
    let left = 0;
    let placement = 'top';

    // Determine vertical position (prefer showing above, then below)
    if (spaceAbove >= pickerHeight + gap) {
      // Show above
      top = button.top - pickerHeight - gap;
      placement = 'top';
    } else if (spaceBelow >= pickerHeight + gap) {
      // Show below
      top = button.bottom + gap;
      placement = 'bottom';
    } else {
      // Not enough space either way, use the side with more space
      if (spaceAbove > spaceBelow) {
        top = Math.max(gap, button.top - pickerHeight - gap);
        placement = 'top';
      } else {
        top = button.bottom + gap;
        // Make sure it doesn't go below viewport
        top = Math.min(top, viewport.height - pickerHeight - gap);
        placement = 'bottom';
      }
    }

    // Determine horizontal position (prefer right alignment, then left, then center)
    if (spaceRight >= pickerWidth) {
      // Align to right of button
      left = button.right - pickerWidth;
    } else if (spaceLeft >= pickerWidth) {
      // Align to left of button
      left = button.left;
    } else {
      // Center horizontally with padding
      left = Math.max(gap, Math.min(button.left, viewport.width - pickerWidth - gap));
    }

    // Ensure picker stays within viewport bounds
    top = Math.max(gap, Math.min(top, viewport.height - pickerHeight - gap));
    left = Math.max(gap, Math.min(left, viewport.width - pickerWidth - gap));

    setPosition({ top, left, placement });
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    // Calculate position when picker becomes visible
    // Use setTimeout to ensure the picker is fully rendered
    const timer = setTimeout(() => {
      calculatePosition();
    }, 10);

    // Recalculate on scroll and resize
    window.addEventListener('scroll', calculatePosition, true);
    window.addEventListener('resize', calculatePosition);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', calculatePosition, true);
      window.removeEventListener('resize', calculatePosition);
    };
  }, [isVisible, calculatePosition]);

  return { pickerRef, buttonRef, position };
};
